import * as React from 'react';
import { useMemo } from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import { useTranslation } from 'react-i18next';
import {
    Row,
    FormInstance,
    Button,
} from 'antd';
import { getInvoiceLevelFieldFromMapping } from '../generic-sub-layout-renderer';
import { invoiceFieldStyles } from './invoice-field.styles';

interface IProps extends StylesProps<ReturnType<typeof invoiceFieldStyles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    params: any;
}

const InvoiceField = (props: IProps) => {
    const { t } = useTranslation();
    const {
        form,
        params,
        classes,
    } = props;

    const invoiceLayout = params?.subLayout?.invoice || [];

    const { invoicesData, setInvoicesData, disabledFields = {} } = params;
    const isInvoiceDisabled = !!disabledFields.invoice;

    const addInvoice = () => {
        setInvoicesData((currentInvoicesData: any[]) => [...currentInvoicesData, {}]);
    };

    const handleRemoveInvoice = (index: number) => {
        if (invoicesData.length <= 1) {
            return;
        }

        const allFields = form.getFieldsValue();
        const updatedFields: any = {};
        const fieldsToRemove: string[] = [];

        const invoiceFieldKeys = Object.keys(allFields).filter((key) => key.startsWith('invoice_') && key.match(/_(\d+)$/));

        invoiceFieldKeys.forEach((fieldKey) => {
            const match = fieldKey.match(/^(.+)_(\d+)$/);
            if (!match) return;

            const [, fieldName, fieldIndex] = match;
            const currentIndex = parseInt(fieldIndex, 10);

            if (currentIndex === index) {
                fieldsToRemove.push(fieldKey);
            } else if (currentIndex > index) {
                const newIndex = currentIndex - 1;
                const newFieldKey = `${fieldName}_${newIndex}`;
                updatedFields[newFieldKey] = allFields[fieldKey];
                fieldsToRemove.push(fieldKey);
            }
        });

        fieldsToRemove.forEach((key) => {
            form.resetFields([key]);
        });

        if (Object.keys(updatedFields).length > 0) {
            form.setFieldsValue(updatedFields);
        }

        const newInvoicesData = [...invoicesData];
        newInvoicesData.splice(index, 1);
        setInvoicesData(newInvoicesData);
    };

    const renderAddMoreButton = () => {
        return (
            <Button
                type="primary"
                onClick={addInvoice}
                className={classes.addMoreButton}
                disabled={isInvoiceDisabled}
            >
                {t('add_more_text')}
            </Button>
        );
    };


    const renderRemoveInvoice = (index: number) => {
        return (
            <Button danger type="link" onClick={() => handleRemoveInvoice(index)} disabled={isInvoiceDisabled}>
                {t('remove')}
            </Button>
        );
    };

    React.useEffect(() => {
        if (invoicesData?.length === 0) {
            addInvoice();
        }
    }, []);

    const invoiceComponent = useMemo(() => (
        <>
            {invoicesData?.map((invoice: any, invoiceIndex: number) => {
                const invoiceForm = invoiceLayout?.map(
                    (fieldKey: any) => getInvoiceLevelFieldFromMapping(fieldKey, form, {
                        ...params,
                        id: invoiceIndex,
                        key: `invoice_${fieldKey}_${invoiceIndex}`,
                        invoiceIndex,
                        pieceIndex: invoiceIndex,
                    }),
                );
                return (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`invoice-${invoiceIndex}`}
                        className={classes.invoiceItem}
                    >
                        <div className={classes.invoiceItemHeader}>
                            <div className={classes.invoiceItemHeaderTitle}>
                                {'Invoice '}
                                {invoiceIndex + 1}
                            </div>
                            {invoicesData?.length !== 1 && renderRemoveInvoice(invoiceIndex)}
                        </div>
                        <Row gutter={20}>
                            {invoiceForm}
                        </Row>
                    </div>
                );
            })}
        </>
    ), [invoicesData, invoiceLayout, params, form]);

    return (
        <>
            {invoiceLayout?.length > 0 && (
                <div className={classes.invoiceFieldContainer}>
                    {invoiceComponent}
                    {renderAddMoreButton()}
                </div>
            )}
        </>
    );
};

const mapStateToProps = () => ({});

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: invoiceFieldStyles,
    },
};

export default GenericHoc(hocConfig)(InvoiceField);

